import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { X, Radio, MapPin, Phone, AlertTriangle, History, Play, Pause, Timer } from "lucide-react";
import LeafletMap from "./LeafletMap";

function timeAgo(iso) {
  if (!iso) return "no ping yet";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

// Haversine (meters) between two lat/lng points
function haversine(a, b) {
  const R = 6371000, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

// Minimum distance from point p to a polyline (array of [lat,lng])
function offRouteMeters(point, route) {
  if (!route || route.length === 0 || !point) return null;
  let min = Infinity;
  for (const [lat, lng] of route) {
    const d = haversine(point, { lat, lng });
    if (d < min) min = d;
  }
  return Math.round(min);
}

export default function LiveTrackModal({ rideId, mode = "live", onClose }) {
  // mode: "live" (poll /location) or "playback" (single /track fetch)
  const [data, setData] = useState(null);
  const [playbackIdx, setPlaybackIdx] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (mode === "playback") {
      api.get(`/rides/${rideId}/track`).then((r) => {
        setData(r.data);
        setPlaybackIdx((r.data.pings || []).length ? 0 : 0);
      }).catch(() => {});
      return;
    }
    const load = () => api.get(`/rides/${rideId}/location`).then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [rideId, mode]);

  // Playback autoplay
  useEffect(() => {
    if (!playing || mode !== "playback") return;
    const pings = data?.pings || [];
    if (playbackIdx >= pings.length - 1) { setPlaying(false); return; }
    const t = setTimeout(() => setPlaybackIdx((i) => Math.min(i + 1, pings.length - 1)), 500);
    return () => clearTimeout(t);
  }, [playing, playbackIdx, data, mode]);

  const isPlayback = mode === "playback";
  const pings = data?.pings || [];
  const currentPing = isPlayback
    ? (pings[playbackIdx] || null)
    : (data?.last_location || null);

  const driverPos = currentPing ? { lat: currentPing.lat, lng: currentPing.lng } : null;
  const trail = isPlayback ? pings.slice(0, playbackIdx + 1).map((p) => [p.lat, p.lng]) : null;

  const off = useMemo(() => {
    if (isPlayback) {
      if (!driverPos || !data?.route_geometry) return null;
      return offRouteMeters(driverPos, data.route_geometry);
    }
    // live: prefer server-side computed value
    if (data?.off_route_meters != null) return data.off_route_meters;
    if (!driverPos || !data?.route_geometry) return null;
    return offRouteMeters(driverPos, data.route_geometry);
  }, [driverPos, data, isPlayback]);

  const offBadge = off == null ? null : off > 300 ? { color: "text-rose-700 bg-rose-50 border-rose-200", label: `${off} m off route` }
    : off > 120 ? { color: "text-amber-700 bg-amber-50 border-amber-200", label: `${off} m off route` }
    : { color: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "On route" };

  const etaBadge = !isPlayback && data?.eta_minutes != null
    ? { minutes: data.eta_minutes, remaining_km: data.remaining_meters != null ? (data.remaining_meters / 1000).toFixed(1) : null }
    : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="live-track-modal">
      <div className="bg-white w-full max-w-4xl rounded-md shadow-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            {isPlayback ? (
              <History size={16} className="text-slate-500" />
            ) : (
              <Radio size={16} className={`${data?.last_location ? "text-emerald-600 animate-pulse" : "text-slate-400"}`} />
            )}
            <div>
              <div className="font-display font-semibold text-slate-900">
                {isPlayback ? "Trip playback" : "Live trip"} · {data?.patient_name}
              </div>
              <div className="text-xs text-slate-500">
                {data?.driver?.name || "Unassigned driver"}
                {isPlayback ? ` · ${pings.length} pings` : currentPing ? ` · updated ${timeAgo(currentPing.at)}` : " · waiting for GPS"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {etaBadge && (
              <span className="text-[11px] font-medium rounded-full px-2 py-0.5 border bg-sky-50 text-sky-800 border-sky-200 inline-flex items-center gap-1" data-testid="eta-badge">
                <Timer size={11} />
                ETA {etaBadge.minutes} min
                {etaBadge.remaining_km ? <span className="text-sky-600/70 ml-1">· {etaBadge.remaining_km} km</span> : null}
              </span>
            )}
            {offBadge && (
              <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 border ${offBadge.color} inline-flex items-center gap-1`} data-testid="off-route-badge">
                {off > 120 ? <AlertTriangle size={11} /> : null} {offBadge.label}
              </span>
            )}
            <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="close-live"><X size={16} /></button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3">
          <div className="md:col-span-2 min-h-[380px]">
            <LeafletMap
              pickup={data?.pickup_coords || null}
              dropoff={data?.dropoff_coords || null}
              route={data?.route_geometry || null}
              driver={driverPos}
              trail={trail}
              height={420}
            />
          </div>
          <div className="p-5 text-sm space-y-3 border-t md:border-t-0 md:border-l border-slate-200">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Pickup</div>
              <div className="flex items-start gap-1.5 mt-0.5"><MapPin size={13} className="text-emerald-600 mt-0.5" /><span>{data?.pickup_address}</span></div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Dropoff</div>
              <div className="flex items-start gap-1.5 mt-0.5"><MapPin size={13} className="text-rose-600 mt-0.5" /><span>{data?.dropoff_address}</span></div>
            </div>
            {data?.driver?.phone && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Driver</div>
                <a href={`tel:${data.driver.phone}`} className="flex items-center gap-1.5 text-[#005E82] hover:underline mt-0.5">
                  <Phone size={13} /> {data.driver.phone}
                </a>
              </div>
            )}
            {currentPing && (
              <div className="pt-3 border-t border-slate-200 font-mono text-[11px] text-slate-500">
                {currentPing.lat.toFixed(5)}, {currentPing.lng.toFixed(5)}
                {currentPing.speed != null && <div>speed {(currentPing.speed * 2.237).toFixed(0)} mph</div>}
                {isPlayback && (
                  <div className="mt-1 text-slate-600">
                    {new Date(currentPing.at).toLocaleString()}
                  </div>
                )}
              </div>
            )}

            {isPlayback && pings.length > 1 && (
              <div className="pt-3 border-t border-slate-200 space-y-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPlaying((p) => !p)}
                    className="w-8 h-8 rounded-full bg-[#0A192F] text-white inline-flex items-center justify-center"
                    data-testid="playback-toggle"
                  >
                    {playing ? <Pause size={14} /> : <Play size={14} />}
                  </button>
                  <span className="text-xs text-slate-500 font-mono">{playbackIdx + 1} / {pings.length}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={pings.length - 1}
                  value={playbackIdx}
                  onChange={(e) => { setPlaying(false); setPlaybackIdx(Number(e.target.value)); }}
                  className="w-full"
                  data-testid="playback-slider"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
